import { motion } from 'framer-motion'
import { useId } from 'react'

import { cn } from '@/lib/utils'

export interface LinePoint {
  label: string
  value: number
}

interface LineProps {
  data: LinePoint[]
  formatValue?: (n: number) => string
  className?: string
  /** viewBox height (width is fixed at 720; the SVG scales to its container) */
  height?: number
  /** render every Nth x-axis label (auto when omitted) */
  labelStep?: number
}

const W = 720
const PAD = { top: 30, right: 22, bottom: 34, left: 22 }
const GRID = [0.25, 0.5, 0.75, 1]

/** Hand-rolled area + line chart. Warm-editorial, viewBox-scaled, strokes stay 1–2px. */
export function Line({
  data,
  formatValue = String,
  className,
  height = 260,
  labelStep,
}: LineProps) {
  const gradId = useId()
  const n = data.length
  if (n === 0) return <EmptyChart className={className} height={height} />

  const innerW = W - PAD.left - PAD.right
  const innerH = height - PAD.top - PAD.bottom
  const baseY = PAD.top + innerH
  const yMax = Math.max(1, ...data.map((d) => d.value)) * 1.15

  const x = (i: number) => (n === 1 ? PAD.left + innerW / 2 : PAD.left + (i / (n - 1)) * innerW)
  const y = (v: number) => PAD.top + innerH - (v / yMax) * innerH

  const pts = data.map((d, i) => ({ px: x(i), py: y(d.value), ...d }))
  const first = pts[0]
  const last = pts[pts.length - 1]
  if (!first || !last) return <EmptyChart className={className} height={height} />

  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.px.toFixed(1)} ${p.py.toFixed(1)}`).join(' ')
  const area = `${line} L${last.px.toFixed(1)} ${baseY} L${first.px.toFixed(1)} ${baseY} Z`
  const step = labelStep ?? Math.max(1, Math.ceil(n / 9))

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className={cn('h-auto w-full overflow-visible', className)}
      role="img"
      aria-label="Grafic de evoluție"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--amber)" stopOpacity="0.34" />
          <stop offset="100%" stopColor="var(--amber)" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {GRID.map((f) => (
        <line
          key={f}
          x1={PAD.left}
          x2={W - PAD.right}
          y1={baseY - f * innerH}
          y2={baseY - f * innerH}
          stroke="var(--hairline)"
          strokeWidth={1}
          strokeDasharray={f === 0 ? undefined : '2 5'}
          vectorEffect="non-scaling-stroke"
        />
      ))}

      <motion.path
        d={area}
        fill={`url(#${gradId})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.25 }}
      />
      <motion.path
        d={line}
        fill="none"
        stroke="var(--amber-strong)"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      />

      {pts.map((p, i) => (
        <motion.circle
          key={`${i}-${p.label}`}
          cx={p.px}
          cy={p.py}
          r={i === n - 1 ? 4.5 : 3}
          fill={i === n - 1 ? 'var(--amber-strong)' : 'var(--card)'}
          stroke="var(--amber-strong)"
          strokeWidth={1.75}
          vectorEffect="non-scaling-stroke"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.5 + i * 0.05 }}
        />
      ))}

      <motion.text
        x={Math.min(last.px, W - PAD.right - 4)}
        y={last.py - 12}
        textAnchor="end"
        className="fill-amber-ink font-mono"
        fontSize={15}
        fontWeight={600}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        {formatValue(last.value)}
      </motion.text>

      {pts.map((p, i) =>
        i % step === 0 || i === n - 1 ? (
          <text
            key={`${i}-${p.label}`}
            x={p.px}
            y={height - 12}
            textAnchor="middle"
            className="fill-taupe"
            fontSize={13}
          >
            {p.label}
          </text>
        ) : null,
      )}
    </svg>
  )
}

function EmptyChart({ className, height }: { className?: string; height: number }) {
  return (
    <div
      className={cn('flex items-center justify-center text-sm text-muted-foreground', className)}
      style={{ height }}
    >
      Nu există date de afișat.
    </div>
  )
}
